import type { Chain } from './chains.js';
import type { Micros } from './money.js';

/**
 * Escrow lifecycle. The only transition that can be driven by an untrusted party
 * is PENDING -> REFUNDED after the deadline, and that is deliberate: the sender's
 * money-back guarantee must not depend on the platform being alive or honest.
 */
export type EscrowStatus =
  | 'NONE'      // free message, no payment attached
  | 'PENDING'   // funded, awaiting reply or deadline
  | 'RELEASED'  // recipient replied in time, paid out minus platform fee
  | 'REFUNDED'; // deadline passed with no reply, returned to sender

export type NotificationChannel = 'telegram' | 'discord' | 'email';

/**
 * Track record, so a sender can judge whether a price is worth paying. Null
 * until there is enough settled history to be honest about — a single answered
 * message should not read as "100% reply rate".
 */
export interface ReplyRecord {
  /** Paid messages that have finished one way or the other. */
  settled: number;
  /** 0–100, share of those that were answered in time. */
  repliedPct: number;
  /** Median minutes from arrival to reply, across answered messages. */
  medianReplyMinutes: number | null;
}

export const MIN_SAMPLES_FOR_RECORD = 3;

export interface PublicProfile {
  /** The wallet address. This is the ONLY identity ever shown to a counterparty. */
  address: string;
  chain: Chain;
  displayName: string | null;
  avatarUrl: string | null;
  bio: string | null;
  /** Zero means an open inbox; anything higher gates the first message. */
  inboxPriceMicros: Micros;
  /** Hours the recipient has to reply before the sender can claw the fee back. */
  replyWindowHours: number;
  verified: boolean;
  /** Whether they have ANY channel linked. Which one is never disclosed. */
  reachable: boolean;
  /** Not accepting paid messages right now. */
  paused: boolean;
  /** Holders of this collection message them for free. */
  freeForCollection: string | null;
  /** Null when there is too little history to report honestly. */
  record: ReplyRecord | null;
}

export interface Message {
  id: string;
  threadId: string;
  senderAddress: string;
  body: string;
  createdAt: string;
  /** Present only on the opening message of a paid thread. */
  escrow: EscrowSummary | null;
}

export interface EscrowSummary {
  id: string;
  status: EscrowStatus;
  amountMicros: Micros;
  /** ISO timestamp after which a refund can be cranked by anyone. */
  deadlineAt: string;
  chain: Chain;
  /** On-chain PDA / contract address holding the funds. */
  vaultAddress: string | null;
  fundingTxSignature: string | null;
  settlementTxSignature: string | null;
}

export interface Thread {
  id: string;
  /** Counterparty as seen by the caller — address only, never their channels. */
  counterpartyAddress: string;
  counterpartyChain: Chain;
  counterpartyDisplayName: string | null;
  lastMessageAt: string;
  lastMessagePreview: string;
  unreadCount: number;
  escrow: EscrowSummary | null;
  /** True when the caller is the one who owes a reply to unlock payment. */
  awaitingMyReply: boolean;
}
